/**
 * Adobe Analytics adapter — the ONLY file that knows about tag IDs and Adobe quirks.
 * Screen code pushes semantic events. This adapter resolves tags and calls GlobalAnalytics.
 */
import { GlobalAnalytics } from "./index";
import { onDataLayerEvent } from "./dataLayer";
import { validateEvent } from "./eventSchema";
import AppSettings from "../appsettings";

const analytics = new GlobalAnalytics();
const isLocal = AppSettings?.hostUrl === "http://localhost";

// ---------------------------------------------------------------------------
// Tag resolution — declarative rules, most-specific first. First match wins.
//
// match fields:
//   event, section, subsection, technologyName — exact string match
//   hasVehicle, hasModule — boolean presence check
//   container — exact string match on interaction.container
//   technologyName: true — any truthy value (wildcard)
//
// Tags:
//   Single value → used for all event types
//   [pageView, click] → picked by event type
//
// To add a new screen: add a rule here, not a tag in screen code.
// ---------------------------------------------------------------------------
const TAG_RULES = [
  // Specialized event types (section-agnostic)
  { match: { event: "article_action" }, tag: "36.3" },
  { match: { event: "article_link_click" }, tag: "28.3" },
  { match: { event: "article_related_click" }, tag: "28.3" },
  { match: { event: "video_event" }, tag: "40.5" },
  { match: { event: "search" }, tag: "28.2" },

  // Product > specific subsections / technology names
  {
    match: { section: "Product", subsection: "TSS Applicability Chart" },
    tag: ["35.1", "29.2"],
  },
  {
    match: { section: "Product", technologyName: "Contact Us" },
    tag: ["35.1", "29.2"],
  },
  // VIN Lookup clicks use the Featured Technology click format (35.2)
  {
    match: { section: "Product", technologyName: "Vin Lookup" },
    tag: ["35.1", "35.2"],
  },
  // Engage XP Tournament — clicks carry the subsection only to resolve 29.2
  // (F29X2 doesn't use it)
  {
    match: { section: "Product", subsection: "EngageXP" },
    tag: ["35.1", "29.2"],
  },
  {
    match: { section: "Product", technologyName: true },
    tag: ["35.1", "29.2"],
  },

  // Product > vehicle detail pages (discriminated by data shape)
  {
    match: {
      section: "Product",
      hasModule: true,
      hasVehicle: true,
      event: "click",
    },
    tag: "29.3",
  },
  {
    match: { section: "Product", hasModule: true, event: "click" },
    tag: "28.3",
  },
  { match: { section: "Product", hasVehicle: true }, tag: ["29.1", "29.2"] },
  { match: { section: "Product" }, tag: ["28.1", "28.2"] },

  // Section-level
  // Browse component uses 29.2 for clicks even under Articles section
  {
    match: { section: "Articles", container: "Browse", event: "click" },
    tag: "29.2",
  },
  { match: { section: "Articles" }, tag: ["36.1", "36.2"] },
  { match: { section: "News" }, tag: "36.1" },
  { match: { section: "Awards" }, tag: "36.1" },
  { match: { section: "Skills" }, tag: ["19.3", "19.5"] },
  { match: { section: "Engage XP" }, tag: "40.1" },
  { match: { section: "Engage Login" }, tag: ["40.1", "28.2"] },
  { match: { section: "Outage Alerts" }, tag: "36.3" },
  { match: { section: "Error", hasModule: true, event: "click" }, tag: "28.3" },
  { match: { section: "Certification", event: "click" }, tag: "38.2" },
  {
    match: { section: "Towing & Payload Calculator", event: "click" },
    tag: "29.2",
  },
  { match: { section: "eBrochure Archive", event: "click" }, tag: "38.2" },
  { match: { section: "Home Page", event: "page_view" }, tag: "32.1" },
  // Glossary landing tiles carry subsection "Landing Page"; the shared
  // subheader also clicks with section "Glossary" and keeps the 28.2 default
  {
    match: { section: "Glossary", subsection: "Landing Page", event: "click" },
    tag: "38.2",
  },
  { match: { section: "Glossary", subsection: "Landing Page" }, tag: "28.1" },
  { match: { section: "Glossary", event: "page_view" }, tag: "32.1" },
  // Legal pages (Terms & Conditions) use their CMS title as the section, so
  // they're matched on subsection, which F40X1 doesn't use
  { match: { event: "page_view", subsection: "Legal" }, tag: "40.1" },
];

const DEFAULT_TAGS = ["28.1", "28.2"];

/** Check if event matches all conditions in a rule's match object. */
function matchesRule(ctx, match) {
  return Object.entries(match).every(([key, expected]) => {
    if (expected === true) {
      return !!ctx[key];
    }
    return ctx[key] === expected;
  });
}

/** Resolve Adobe tag ID from event shape. Screens never pass tags. */
function resolveTag(event) {
  const isPageView = event.event === "page_view";
  const ctx = {
    event: event.event,
    section: event.page?.section,
    subsection: event.page?.subsection,
    technologyName: event.page?.technologyName,
    container: event.interaction?.container,
    hasModule: !!event.interaction?.module,
    hasVehicle: !!event.product?.modelName,
  };
  const tagIndex = isPageView ? 0 : 1;

  for (const rule of TAG_RULES) {
    if (matchesRule(ctx, rule.match)) {
      return Array.isArray(rule.tag) ? rule.tag[tagIndex] : rule.tag;
    }
  }

  return DEFAULT_TAGS[tagIndex];
}

// ---------------------------------------------------------------------------
// Adobe-specific section defaults — things screens shouldn't know about
// ---------------------------------------------------------------------------
const SECTION_DEFAULTS = {
  Articles: { modelName: " ", container: " ", tilePosition: " " },
};

// ---------------------------------------------------------------------------
// Event → GlobalAnalytics param mapping
// ---------------------------------------------------------------------------

/** Nested event namespace → flat GlobalAnalytics field names */
const FIELD_MAP = {
  page: {
    name: "page",
    section: "section",
    subsection: "subsection",
    technologyName: "technologyName",
  },
  product: {
    modelName: "modelName",
    modelYear: "modelYear",
    modelCode: "modelCode",
    trim: "trim",
    modelEngine: "modelEngine",
    modelCab: "modelCab",
    modelBed: "modelBed",
    modelDrivetrain: "modelDrivetrain",
  },
  interaction: {
    category: "category",
    label: "label",
    container: "container",
    module: "module",
    action: "action",
    tilePosition: "tilePosition",
  },
  content: {
    title: "contentTitle",
    mediaTitle: "mediaTitle",
    categoryTopic: "categoryTopic",
    articleType: "articleType",
    description: "description",
  },
  search: { keyword: "keyword", resultCount: "numberOfSearchResults" },
  video: { event: "events", length: "videoLength" },
  user: { positionTitle: "positionTitle" },
};

function mapToAnalyticsParams(event) {
  const params = { tag: event.tag || resolveTag(event) };

  // Top-level fields that don't belong to a namespace
  if (event.app !== undefined) {
    params.app = event.app;
  }

  // Flatten nested event fields into the param shape GlobalAnalytics expects
  for (const [namespace, fields] of Object.entries(FIELD_MAP)) {
    const source = event[namespace];
    if (!source) {
      continue;
    }
    for (const [srcKey, destKey] of Object.entries(fields)) {
      if (source[srcKey] !== undefined) {
        params[destKey] = source[srcKey];
      }
    }
  }

  // Apply Adobe-specific section defaults
  const defaults = SECTION_DEFAULTS[params.section];
  if (defaults) {
    for (const [key, val] of Object.entries(defaults)) {
      params[key] = params[key] ?? val;
    }
  }

  return params;
}

// ---------------------------------------------------------------------------
// Init — wire data layer to GlobalAnalytics
// ---------------------------------------------------------------------------
export function initAdobeAdapter() {
  onDataLayerEvent((event) => {
    if (!event?.event) {
      return;
    }
    if (isLocal) {
      validateEvent(event);
    }

    const params = mapToAnalyticsParams(event);

    if (event.event === "page_view") {
      analytics.firePageLoad(params);
    } else {
      analytics.fireOnClick(params);
    }
  });
}
